/**
 * Dashboard Copy — Readiness-based emotional copy for the hero card.
 *
 * Provides tier-based messaging that encourages or guides the user
 * depending on their composite readiness score (0–100).
 *
 * Tiers (per FS-011 §3.1):
 *   90–100  → Excellent progress
 *   75–89   → On track
 *   60–74   → Getting there
 *   <60     → Needs attention
 */

/** A single readiness copy tier with headline and description. */
export interface ReadinessCopy {
  /** Tier key for programmatic use (e.g. CSS classes, analytics). */
  tier: "excellent" | "on-track" | "getting-there" | "needs-attention";
  /** Short headline displayed prominently on the hero card. */
  headline: string;
  /** Supportive description providing context or suggesting action. */
  description: string;
  /** Whether this tier should show a link to recommendations. */
  showRecommendationsLink: boolean;
}

/**
 * Return the readiness copy tier for a given score.
 *
 * @param readinessScore  Composite readiness score 0–100.
 * @returns The matching {@link ReadinessCopy} object.
 */
export function getReadinessCopy(readinessScore: number): ReadinessCopy {
  if (readinessScore >= 90) {
    return {
      tier: "excellent",
      headline: "Excellent progress",
      description: "You're on track for a comfortable retirement.",
      showRecommendationsLink: false,
    };
  }

  if (readinessScore >= 75) {
    return {
      tier: "on-track",
      headline: "On track",
      description: "Make a few adjustments to strengthen your plan.",
      showRecommendationsLink: false,
    };
  }

  if (readinessScore >= 60) {
    return {
      tier: "getting-there",
      headline: "Getting there",
      description: "Increase savings or delay retirement slightly.",
      showRecommendationsLink: false,
    };
  }

  return {
    tier: "needs-attention",
    headline: "Needs attention",
    description: "Let's adjust your plan.",
    showRecommendationsLink: true,
  };
}
